<template>
  <div class="checkpoint-microlearning-video-card">
    <!-- Action Buttons (hidden in V2 when parent already shows Generate / ⋮ bar above) -->
    <MicrolearningVideoActionsBar
      v-if="!hideActionsBar && !isVideoProcessing"
      :hasInstructions="hasMicrolearningInstructions"
      @suggest-instructions="handleSuggestInstructions"
      @remove-video-instructions="handleRemoveVideoInstructions"
      @set-video-to-be-instructions="handleSetVideoToBeInstructions"
      @replace="handleReplace"
      @delete="handleDelete"
    />
    <!-- <div class="video-card-actions">
      <div class="video-card-left-actions">
        <AddORAskIQButton
          :label="'Suggest Instructions from video'"
          @addAI="handleSuggestInstructions"
          :showAddButton="false"
        />
      </div>
      <div class="video-card-right-actions">
        <StandardButton
          type="magenta"
          variant="outline"
          size="small"
          label="Replace"
          :disabled="isVideoProcessing"
          @click="handleReplace"
        />
        <StandardButton
          type="red"
          variant="outline"
          size="small"
          label="Delete"
          :disabled="isVideoProcessing"
          @click="handleDelete"
        />
      </div>
    </div> -->

    <!-- Microlearning Instructions View -->
    <div v-if="hasMicrolearningInstructions" class="microlearning-instructions-view">
      <MicrolearningViewEdit
        :video="parsedInstructions.video_url || videoUrl"
        :error="parsedInstructions.error || null"
        :summary="parsedInstructions.summary || null"
        :steps="localSteps"
        :editable="true"
        @step-click="handleStepClick"
      />
    </div>

    <!-- Video Processing / No Instructions State -->
    <div v-else>
      <!-- Compact processing loader (matches CheckpointEditHeader MDP-452 style) -->
      <div v-if="isVideoProcessing && !hasVideo" class="video-processing-compact">
        <div class="video-processing-compact__loader-wrap" aria-hidden="true">
          <StreamProcessingLoaderSvg class="video-processing-compact__loader" />
        </div>
        <p class="video-processing-compact__text">Video is being processed ...</p>
      </div>

      <!-- Video Player when video exists and is a valid URL -->
      <div v-else-if="hasVideo" class="microlearning-video-player">
        <div
          class="microlearning-video-container"
          :class="{ 'video-processing-compact-wrap': isVideoProcessing }"
        >
          <div v-if="isVideoProcessing" class="video-processing-compact video-processing-compact--overlay">
            <div class="video-processing-compact__loader-wrap" aria-hidden="true">
              <StreamProcessingLoaderSvg class="video-processing-compact__loader" />
            </div>
            <p class="video-processing-compact__text">Video is being processed ...</p>
          </div>

          <!-- If it's an iframe embed (contains <iframe>) -->
          <div v-if="isIframeEmbed && !isVideoProcessing" v-html="videoUrl" class="microlearning-iframe-container"></div>
          <!-- If it's a direct video URL, embed as iframe (matches CheckpointInspectionInstructions) -->
          <iframe
            v-else-if="!isVideoProcessing"
            :key="playbackVideoUrl"
            :src="playbackVideoUrl"
            class="microlearning-video"
            allowfullscreen
            allow="
              accelerometer;
              autoplay;
              clipboard-write;
              encrypted-media;
              gyroscope;
              picture-in-picture;
            "
            frameborder="0"
          />
        </div>
      </div>

      <!-- No video placeholder -->
      <div v-else class="no-microlearning-content">
        <p>
          No microlearning video available. Click "Add Microlearning Video" above to upload one.
        </p>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <DeleteMicrolearning
      v-model="showDeleteDialog"
      @onDelete="confirmDelete"
      @input="showDeleteDialog = false"
    />

    <!-- Inspection Step Dialog -->
    <InspectionStepDialog
      :value="showInspectionDialog"
      :state="inspectionDialogState"
      :category="dialogCategory"
      :inspect="dialogInspect"
      :lookFor="dialogLookFor"
      :passIf="dialogPassIf"
      :failIf="dialogFailIf"
      :callApiOnSave="true"
      @input="showInspectionDialog = $event"
      @close="handleInspectionDialogClose"
      @discard="handleInspectionDiscard"
      @submit="handleInspectionSubmit"
    />

    <SetVideoToBeInstructionsDialog
      :value="showSetVideoToBeInstructionsDialog"
      headline="Set Video to be the Instructions"
      info="You are about to set this Video and its Instructions to be the Checkpoint Instructions. This action is irreversible."
      actionLabel="Set"
      @onAction="confirmSetVideoToBeInstructions"
      @input="handleSetVideoToBeInstructionsDialogInput($event)"
    />

    <!-- Remove Video Instructions Dialog -->
    <RemoveVideoInstructionsDialog
      v-model="showRemoveInstructionsDialog"
      :destructive="true"
      headline="Remove Video Instructions"
      info="You are about to remove Video Instructions. Are you sure? This action is irreversible."
      actionLabel="Remove Instructions"
      @onAction="confirmRemoveVideoInstructions"
      @input="handleRemoveInstructionsDialogInput($event)"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted, onMounted, getCurrentInstance } from "vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import DeleteMicrolearning from "@components/RolesCatalog/DeleteMicrolearning.vue";
import AddORAskIQButton from "@components/AddORAskIQButton/AddORAskIQButton.vue";
import { useIQOverlayStore } from "@/store/pinia/iqOverlay";
import MicrolearningVideoActionsBar from "../MicrolearningVideoActionsBar/MicrolearningVideoActionsBar.vue";
import MicrolearningViewEdit from "@components/MicrolearningViewEdit/MicrolearningViewEdit.vue";
import InspectionStepDialog from "@components/InspectionStepDialog/InspectionStepDialog.vue";
import RemoveVideoInstructionsDialog from "@components/RemoveVideoInstructionsDialog/RemoveVideoInstructionsDialog.vue";
import {
  changeCheckpointV2,
  deleteProposedVideo,
  getCheckpointVideoSignedUrl,
} from "@/api/responsibilityAPI";
import { isCloudflareSignedUrl } from "@/utils/simulatedCloudflareStreamWebhook";
import { needsSignedVideoUrlResolution } from "@/utils/activeCheckpointMicrolearningValidity";
import SetVideoToBeInstructionsDialog from "@components/SetVideoToBeInstructionsDialog/SetVideoToBeInstructionsDialog.vue";
import StreamProcessingLoaderSvg from "@components/StreamProcessingLoaderSvg/StreamProcessingLoaderSvg.vue";
// Get Vue instance for $loader
const app = getCurrentInstance();
const { proxy } = app || {};
const props = defineProps({
  videoUrl: {
    type: String,
    default: null,
  },
  videoId: {
    type: [String, Number],
    default: null,
  },
  // Checkpoint ID for API calls
  checkpointId: {
    type: [String, Number],
    default: null,
  },
  // Responsibility ID for API calls
  responsibilityId: {
    type: [String, Number],
    default: null,
  },
  // Checkpoint data for IQ prompt
  checkpointPrefix: {
    type: String,
    default: "",
  },
  checkpointCriteria: {
    type: String,
    default: "",
  },
  // Responsibility data for IQ prompt
  responsibilityCode: {
    type: String,
    default: "",
  },
  responsibilityName: {
    type: String,
    default: "",
  },
  responsibilityPurpose: {
    type: String,
    default: "",
  },
  // Checkpoint instructions for IQ prompt (business_purpose)
  checkpointInstructions: {
    type: String,
    default: "",
  },
  microlearningInstructions: {
    type: [String, Object],
    default: null,
  },
  setToMicrolearningInstructions: {
    type: Boolean,
    default: false,
  },
  /** When true, omit Suggest/Replace/Delete row (parent owns those actions). */
  hideActionsBar: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "replace",
  "delete",
  "video-play",
  "video-pause",
  "video-ended",
  "refresh-checkpoint",
  "onAddIQ",
]);

const videoPlayer = ref(null);
const showDeleteDialog = ref(false);

// Inspection Step Dialog state
const showInspectionDialog = ref(false);
const selectedStep = ref(null);
const selectedStepIndex = ref(-1);

// Remove Video Instructions Dialog state
const showRemoveInstructionsDialog = ref(false);

// Set Video to be the Instructions Dialog state
const showSetVideoToBeInstructionsDialog = ref(false);
// Local copy of steps for editing
const localSteps = ref([]);

// Video loading state
const videoLoadError = ref(false);
const videoLoadAttempts = ref(0);
const MAX_LOAD_ATTEMPTS = 3; // Maximum attempts before treating as processing
const isVideoReady = ref(false);
const pollingStartedForUrl = ref(null); // Track which URL we're polling for
const hasStartedPollingForCurrentUrl = ref(false); // Track if we've started polling for current URL

// Computed dialog state - Edit mode when step has data
const inspectionDialogState = computed(() => {
  if (!selectedStep.value) return "Empty";
  return "Edit";
});

// Computed properties for dialog props (avoiding optional chaining in template)
const dialogCategory = computed(() => {
  return selectedStep.value && selectedStep.value.competence_category
    ? selectedStep.value.competence_category
    : "knowledge";
});

const dialogInspect = computed(() => {
  return selectedStep.value && selectedStep.value.inspector_action
    ? selectedStep.value.inspector_action
    : "";
});

const dialogLookFor = computed(() => {
  return selectedStep.value && selectedStep.value.look_for ? selectedStep.value.look_for : "";
});

const dialogPassIf = computed(() => {
  return selectedStep.value && selectedStep.value.pass_criteria
    ? selectedStep.value.pass_criteria
    : "";
});

const dialogFailIf = computed(() => {
  return selectedStep.value && selectedStep.value.fail_criteria
    ? selectedStep.value.fail_criteria
    : "";
});

// Computed to check if video exists and is a valid URL (not just a processing message)
const hasVideo = computed(() => {
  if (!props.videoUrl || props.videoUrl.length === 0) return false;

  // If it's just a processing message, don't treat it as a video URL
  const isProcessingMessage =
    props.videoUrl.includes("being processed") ||
    props.videoUrl.includes("will be available in a moment") ||
    props.videoUrl.includes("processing");

  // If it's an iframe embed, it's valid
  if (isIframeEmbed.value) return true;

  // If it's a processing message, don't show video element (only show processing overlay)
  if (isProcessingMessage) return false;

  // Otherwise, it's a valid video URL
  return true;
});
// Parse microlearning instructions - handles both string (JSON) and object
const parsedInstructions = computed(() => {
  if (!props.microlearningInstructions) return {};
  const instructions = props.microlearningInstructions;
  // If it's a string, try to parse as JSON
  if (typeof instructions === "string") {
    try {
      return JSON.parse(instructions);
    } catch (e) {
      return {};
    }
  }
  // If it's already an object, return as-is
  if (typeof instructions === "object" && instructions !== null) {
    return instructions;
  }
  return {};
});

// Check if microlearning instructions have meaningful content - handles empty object {} case
const hasMicrolearningInstructions = computed(() => {
  const instructions = parsedInstructions.value;
  // Check if it's an object with keys (not empty {})
  if (Object.keys(instructions).length > 0) {
    // Must have either steps or summary to be considered valid
    return !!(instructions.steps?.length > 0 || instructions.summary);
  }
  return false;
});

// Computed to check if the video content is an iframe HTML string (not just a URL containing "iframe")
const isIframeEmbed = computed(() => {
  if (!props.videoUrl) return false;
  return props.videoUrl.trim().toLowerCase().includes("<iframe");
});

// Cloudflare Stream now requires a freshly signed playback URL (requireSignedURLs) —
// resolve it before rendering the iframe instead of using the raw checkpoint video value.
const resolvedVideoUrl = ref(null);

const fetchSignedVideoUrl = async () => {
  if (!props.checkpointId || !props.responsibilityId) {
    resolvedVideoUrl.value = null;
    return;
  }
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    if (!accountId) {
      resolvedVideoUrl.value = null;
      return;
    }
    const response = await getCheckpointVideoSignedUrl(
      accountId,
      props.responsibilityId,
      props.checkpointId
    );
    resolvedVideoUrl.value = response?.data?.url ?? null;
  } catch (error) {
    console.error("Error fetching video signed URL:", error);
    resolvedVideoUrl.value = null;
  }
};

// The iframe src to render: a freshly signed URL when there is a playable video that isn't
// already iframe HTML or a signed URL, otherwise fall back to the raw prop value.
const playbackVideoUrl = computed(() => resolvedVideoUrl.value || props.videoUrl);

watch(
  () => props.videoUrl,
  (newUrl) => {
    if (hasVideo.value && needsSignedVideoUrlResolution(newUrl, isCloudflareSignedUrl)) {
      fetchSignedVideoUrl();
    } else {
      resolvedVideoUrl.value = null;
    }
  },
  { immediate: true }
);

// Computed to check if video is still being processed by api.video
const isVideoProcessing = computed(() => {
  if (!props.videoUrl) return false;

  // Check for explicit processing messages in URL (from API)
  const hasProcessingMessage =
    props.videoUrl.includes("being processed") ||
    props.videoUrl.includes("will be available in a moment") ||
    props.videoUrl.includes("processing");

  // If video failed to load and we haven't exceeded max attempts, treat as processing
  // This handles the case where API returns a URL but video isn't ready yet
  const hasLoadError =
    videoLoadError.value && videoLoadAttempts.value < MAX_LOAD_ATTEMPTS && !isVideoReady.value;

  return hasProcessingMessage || hasLoadError;
});

// Polling interval reference
let pollingInterval = null;
const POLLING_INTERVAL_MS = 5000; // Check every 5 seconds

// Start polling for processing message in URL (from API)
const startPollingForProcessingMessage = () => {
  // Don't start if already polling
  if (pollingInterval) {
    return;
  }

  // Don't start if we've already started polling for this URL
  const currentUrl = props.videoUrl;
  if (
    currentUrl &&
    hasStartedPollingForCurrentUrl.value &&
    pollingStartedForUrl.value === currentUrl
  ) {
    return;
  }

  // Check if URL contains processing message
  const hasProcessingMessage =
    currentUrl &&
    (currentUrl.includes("being processed") ||
      currentUrl.includes("will be available in a moment") ||
      currentUrl.includes("processing"));

  if (!hasProcessingMessage) {
    return;
  }

  // Mark that we're polling for this URL
  if (currentUrl) {
    pollingStartedForUrl.value = currentUrl;
    hasStartedPollingForCurrentUrl.value = true;
  }

  pollingInterval = setInterval(() => {
    // Check if still processing
    const stillProcessing =
      props.videoUrl &&
      (props.videoUrl.includes("being processed") ||
        props.videoUrl.includes("will be available in a moment") ||
        props.videoUrl.includes("processing"));

    // Only refresh if still processing and same URL
    if (stillProcessing && props.videoUrl === pollingStartedForUrl.value) {
      emit("refresh-checkpoint");
    } else if (
      !stillProcessing &&
      props.videoUrl &&
      props.videoUrl !== pollingStartedForUrl.value
    ) {
      // URL changed from processing message to actual URL
      // Continue polling a bit more to ensure video is ready, then stop
      // Give it one more refresh cycle to ensure video loads
      emit("refresh-checkpoint");
      setTimeout(() => {
        stopPolling();
      }, 1000);
    } else {
      // Video is ready (no processing message), stop polling
      stopPolling();
    }
  }, POLLING_INTERVAL_MS);
};

// Start polling when video element fails to load
const startPolling = () => {
  // Don't start if already polling
  if (pollingInterval) {
    return;
  }

  // Don't start if we've already started polling for this URL
  const currentUrl = props.videoUrl;
  if (
    currentUrl &&
    hasStartedPollingForCurrentUrl.value &&
    pollingStartedForUrl.value === currentUrl
  ) {
    return;
  }

  // Only poll if video actually failed to load (not just because URL has processing message)
  // If URL contains processing message, use startPollingForProcessingMessage instead
  if (!videoLoadError.value) {
    return;
  }

  // Mark that we're polling for this URL
  if (currentUrl) {
    pollingStartedForUrl.value = currentUrl;
    hasStartedPollingForCurrentUrl.value = true;
  }

  pollingInterval = setInterval(() => {
    // Only refresh if:
    // 1. Video is still processing (has load error)
    // 2. We're still on the same URL
    // 3. We haven't exceeded max attempts
    if (
      videoLoadError.value &&
      videoLoadAttempts.value < MAX_LOAD_ATTEMPTS &&
      !isVideoReady.value &&
      props.videoUrl === pollingStartedForUrl.value
    ) {
      emit("refresh-checkpoint");
    } else {
      // Video is ready, URL changed, or max attempts reached - stop polling
      stopPolling();
    }
  }, POLLING_INTERVAL_MS);
};

// Stop polling
const stopPolling = () => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
  // Don't reset pollingStartedForUrl here - we keep it to prevent restarting for same URL
};

// Watch for video URL changes - reset loading state when URL changes
watch(
  () => props.videoUrl,
  (newUrl, oldUrl) => {
    // If URL changed, reset loading state and stop any existing polling
    if (newUrl !== oldUrl) {
      // Stop polling for old URL
      stopPolling();

      // Reset loading state for new URL
      videoLoadError.value = false;
      videoLoadAttempts.value = 0;
      isVideoReady.value = false;
      hasStartedPollingForCurrentUrl.value = false;
      pollingStartedForUrl.value = null;

      // If URL changed from processing message to actual URL, reload video element
      const wasProcessing =
        oldUrl &&
        (oldUrl.includes("being processed") ||
          oldUrl.includes("will be available in a moment") ||
          oldUrl.includes("processing"));
      const isNowValidUrl =
        newUrl &&
        !newUrl.includes("being processed") &&
        !newUrl.includes("will be available in a moment") &&
        !newUrl.includes("processing") &&
        !isIframeEmbed.value;

      // If we transitioned from processing to valid URL, force video reload
      if (wasProcessing && isNowValidUrl && videoPlayer.value) {
        // Force video element to reload with new URL
        videoPlayer.value.load();
      }

      // If new URL contains processing message, start polling immediately
      // This handles the case where video is just uploaded and API returns processing message
      if (
        newUrl &&
        (newUrl.includes("being processed") ||
          newUrl.includes("will be available in a moment") ||
          newUrl.includes("processing"))
      ) {
        // Start polling immediately to check when video becomes available
        // Use a small delay to allow component to settle
        setTimeout(() => {
          if (!hasStartedPollingForCurrentUrl.value && props.videoUrl === newUrl) {
            // Trigger immediate refresh first
            emit("refresh-checkpoint");
            // Then start polling
            startPollingForProcessingMessage();
          }
        }, 500);
      }
    }
  },
  { immediate: false }
);

// Watch for video processing state changes - but only start polling if video actually fails
// Don't start polling just because URL contains processing message (that's handled by parent)
watch(
  isVideoProcessing,
  (isProcessing) => {
    if (isProcessing) {
      // Only start polling if video failed to load (not just because URL has processing message)
      // This ensures we only poll when we actually need to (video load error)
      if (videoLoadError.value && !hasStartedPollingForCurrentUrl.value) {
        startPolling();
      }
    } else {
      // Video is ready, stop polling
      stopPolling();
    }
  },
  { immediate: false } // Don't run immediately - wait for actual video load attempt
);

// Initialize localSteps from parsedInstructions
watch(
  () => parsedInstructions.value.steps,
  (newSteps) => {
    if (newSteps && Array.isArray(newSteps)) {
      // Deep copy to avoid mutating props
      localSteps.value = JSON.parse(JSON.stringify(newSteps));
    } else {
      localSteps.value = [];
    }
  },
  { immediate: true, deep: true }
);

// Cleanup on unmount - stop all polling
onUnmounted(() => {
  stopPolling();
  // Reset polling state
  hasStartedPollingForCurrentUrl.value = false;
  pollingStartedForUrl.value = null;
  if (refreshCheckpointUnsubscribe) {
    refreshCheckpointUnsubscribe();
  }
});

// IQ Overlay store
const iqOverlayStore = useIQOverlayStore();

// Subscribe to refresh checkpoint event from IQOverlay
let refreshCheckpointUnsubscribe = null;
onMounted(() => {
  refreshCheckpointUnsubscribe = iqOverlayStore.onRefreshCheckpoint(() => {
    // Refresh checkpoint when video instructions are saved from IQOverlay
    emit("refresh-checkpoint");
  });

  // Check if video URL contains processing message on mount
  // This handles the case where component is mounted with a processing URL
  if (
    props.videoUrl &&
    (props.videoUrl.includes("being processed") ||
      props.videoUrl.includes("will be available in a moment") ||
      props.videoUrl.includes("processing"))
  ) {
    // Start polling immediately if URL contains processing message
    setTimeout(() => {
      if (!hasStartedPollingForCurrentUrl.value && props.videoUrl) {
        startPollingForProcessingMessage();
        // Also trigger immediate refresh
        emit("refresh-checkpoint");
      }
    }, 500);
  }
});

// Cleanup subscription on unmount
onUnmounted(() => {
  if (refreshCheckpointUnsubscribe) {
    refreshCheckpointUnsubscribe();
  }
  stopPolling();
});

// Handle step click from MicrolearningViewEdit
const handleStepClick = ({ step, index }) => {
  selectedStep.value = { ...step };
  selectedStepIndex.value = index;
  showInspectionDialog.value = true;
};

// Handle inspection dialog close
const handleInspectionDialogClose = () => {
  showInspectionDialog.value = false;
  selectedStep.value = null;
  selectedStepIndex.value = -1;
};

// Handle discard in inspection dialog - just close without saving
const handleInspectionDiscard = () => {
  // Reset to original step data (no changes saved)
  handleInspectionDialogClose();
};

// Handle submit in inspection dialog - update local step data and save to API
const handleInspectionSubmit = async (formData) => {
  if (selectedStepIndex.value >= 0 && selectedStepIndex.value < localSteps.value.length) {
    // Update the step at the selected index with new data
    const updatedStep = {
      ...localSteps.value[selectedStepIndex.value],
      competence_category: formData.category,
      inspector_action: formData.inspect,
      look_for: formData.lookFor,
      pass_criteria: formData.passIf,
      fail_criteria: formData.failIf,
    };

    // Update the local steps array
    localSteps.value = [
      ...localSteps.value.slice(0, selectedStepIndex.value),
      updatedStep,
      ...localSteps.value.slice(selectedStepIndex.value + 1),
    ];

    // Build complete microlearning instructions JSON with updated step
    const completeInstructions = {
      ...parsedInstructions.value,
      steps: localSteps.value,
    };

    // Call API to save the updated instructions
    try {
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      if (accountId && props.checkpointId && props.responsibilityId) {
        const payload = {
          microlearning_instructions: completeInstructions,
        };
        await changeCheckpointV2(accountId, props.responsibilityId, props.checkpointId, payload);

        // Emit refresh event to update parent component
        emit("refresh-checkpoint");
      }
    } catch (error) {
      console.error("Error saving inspection step:", error);
      // Optionally show error message to user
    }
  }

  // Close the dialog
  handleInspectionDialogClose();
};

// Handle "Suggest Instructions from video" click
const handleSuggestInstructions = () => {
  // Build the prompt with checkpoint and responsibility info
  const checkpointInfo = [props.checkpointPrefix, props.checkpointCriteria]
    .filter(Boolean)
    .join(" ");

  const responsibilityInfo = [props.responsibilityCode, props.responsibilityName]
    .filter(Boolean)
    .join(" ");

  const prompt = `Pls suggest Inspection instructions from video of Checkpoint: ${checkpointInfo} belonging to responsibility ${responsibilityInfo}`;

  // Open IQ overlay with the prompt and video instructions type
  iqOverlayStore.openOverlay({
    prompt: prompt,
    type: "videoInstructions",
    useCustomLoader: true,
    videoUrl: props.videoUrl,
    checkpointId: props.checkpointId,
    responsibilityId: props.responsibilityId,
    checkpointPrefix: props.checkpointPrefix,
    checkpointCriteria: props.checkpointCriteria,
    responsibilityCode: props.responsibilityCode,
    responsibilityName: props.responsibilityName,
    responsibilityPurpose: props.responsibilityPurpose,
    checkpointInstructions: props.checkpointInstructions,
  });

  // Also emit the event for parent components
  emit("onAddIQ");
};

// Action handlers
const handleReplace = () => {
  emit("replace");
};

const handleDelete = () => {
  // Show confirmation dialog instead of directly emitting
  showDeleteDialog.value = true;
};

const confirmDelete = async () => {
  if (!props.checkpointId) {
    console.error("Missing checkpoint ID for deletion");
    return;
  }

  try {
    proxy.$loader.setAppLoading(true);
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

    // Delete the proposed video first
    await deleteProposedVideo(accountId, props.checkpointId);

    // If set_to_microlearning_instructions is true, clear both instructions and microlearning_instructions
    if (props.setToMicrolearningInstructions) {
      const payload = {
        instructions: null,
        microlearning_instructions: null,
        set_to_microlearning_instructions: false,
      };
      await changeCheckpointV2(accountId, props.responsibilityId, props.checkpointId, payload);
    } else {
      // If set_to_microlearning_instructions is false, just clear microlearning_instructions
      const payload = {
        microlearning_instructions: null,
      };
      await changeCheckpointV2(accountId, props.responsibilityId, props.checkpointId, payload);
    }

    showDeleteDialog.value = false;
    emit("refresh-checkpoint");
  } catch (error) {
    console.error("Error deleting microlearning video:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

// Handle set video to be the instructions
const handleSetVideoToBeInstructions = () => {
  // Show confirmation dialog
  showSetVideoToBeInstructionsDialog.value = true;
};

// Handle remove video instructions
const handleRemoveVideoInstructions = () => {
  // Show confirmation dialog
  showRemoveInstructionsDialog.value = true;
};

const handleRemoveInstructionsDialogInput = (value) => {
  showRemoveInstructionsDialog.value = value;
};

// Handle set video to be the instructions dialog input
const handleSetVideoToBeInstructionsDialogInput = (value) => {
  console.log("handleSetVideoToBeInstructionsDialogInput", value);
  showSetVideoToBeInstructionsDialog.value = value;
};

const confirmSetVideoToBeInstructions = async () => {
  try {
    proxy.$loader.setAppLoading(true);

    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    if (accountId && props.checkpointId && props.responsibilityId) {
      const payload = {
        instructions: JSON.stringify(props.microlearningInstructions),
        set_to_microlearning_instructions: true,
      };
      await changeCheckpointV2(accountId, props.responsibilityId, props.checkpointId, payload);
      emit("refresh-checkpoint");
    }
  } catch (error) {
    console.error("Error setting video to be the instructions:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
    showSetVideoToBeInstructionsDialog.value = false;
  }
};
const confirmRemoveVideoInstructions = async () => {
  try {
    proxy.$loader.setAppLoading(true);

    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    if (accountId && props.checkpointId && props.responsibilityId) {
      const payload = {
        microlearning_instructions: {},
      };
      await changeCheckpointV2(accountId, props.responsibilityId, props.checkpointId, payload);
      emit("refresh-checkpoint");
    }
  } catch (error) {
    console.error("Error removing video instructions:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
    showRemoveInstructionsDialog.value = false;
  }
};

// Video player event handlers
const handleVideoPlay = () => {
  emit("video-play");
};

const handleVideoPause = () => {
  emit("video-pause");
};

const handleVideoEnded = () => {
  emit("video-ended");
};

// Handle video load start
const handleVideoLoadStart = () => {
  // Reset error state when starting to load
  videoLoadError.value = false;
};

// Handle video can play (ready to play)
const handleVideoCanPlay = () => {
  // Video can play, mark as ready
  isVideoReady.value = true;
  videoLoadError.value = false;
  videoLoadAttempts.value = 0;
  stopPolling(); // Stop polling if video is ready
};

// Handle video successfully loaded
const handleVideoLoaded = () => {
  // Video loaded successfully, mark as ready
  isVideoReady.value = true;
  videoLoadError.value = false;
  videoLoadAttempts.value = 0;
  stopPolling(); // Stop polling if video is ready
};

// Handle video load error
const handleVideoError = (event) => {
  console.warn("Video load error:", event);
  videoLoadError.value = true;
  videoLoadAttempts.value += 1;
  isVideoReady.value = false;

  // If we haven't exceeded max attempts, treat as processing and start polling
  if (videoLoadAttempts.value < MAX_LOAD_ATTEMPTS) {
    // Only start polling once per URL - this prevents multiple polling intervals
    if (!hasStartedPollingForCurrentUrl.value || pollingStartedForUrl.value !== props.videoUrl) {
      startPolling();
    }

    // Also try to refresh checkpoint data immediately (once)
    if (videoLoadAttempts.value === 1) {
      emit("refresh-checkpoint");
    }
  } else {
    // Max attempts reached, stop polling
    stopPolling();
    console.error("Video failed to load after multiple attempts");
  }
};

defineExpose({
  videoPlayer,
});
</script>

<style scoped>
.checkpoint-microlearning-video-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.video-card-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.video-card-right-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
}

/* Video Player Styles */
.microlearning-video-player {
  width: 100%;
}

.microlearning-video-container {
  border-radius: 8px;
  overflow: hidden;
  background: black;
  max-width: 100%;
}

.microlearning-video {
  width: 100%;
  height: 400px;
  display: block;
  border: none;
  border-radius: 8px;
  background: black;
}

.microlearning-iframe-container {
  width: 100%;
  min-height: 400px;
}

.microlearning-iframe-container :deep(iframe) {
  width: 100%;
  min-height: 400px;
  border: none;
  border-radius: 8px;
}

.no-microlearning-content {
  padding: 24px;
  text-align: center;
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 14px;
  background-color: #f9fafb;
  border-radius: 8px;
  border: 1px dashed #d0d5dd;
}

.microlearning-video-container {
  position: relative;
}

/* Compact video processing loader (aligned with CheckpointEditHeader) */
.video-processing-compact {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 100px;
  margin-top: 8px;
  padding: 12px 8px 16px;
  box-sizing: border-box;
  background: transparent;
}

.video-processing-compact--overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  margin-top: 0;
  background: #f9fafb;
  border-radius: 8px;
}

.video-processing-compact-wrap {
  position: relative;
  min-height: 120px;
}

.video-processing-compact__loader-wrap {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-processing-compact__loader {
  width: 36px;
  height: 36px;
  display: block;
  flex-shrink: 0;
}

.video-processing-compact__text {
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
  text-align: center;
}
</style>
